import { useEffect, useState, useCallback } from "react";
import { api, ACCOUNT_COLORS, ACCOUNT_LABEL } from "@/lib/api";

export default function Leaderboard({ leagueId, refreshKey = 0 }) {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!leagueId) return;
    api.getLeaderboard(leagueId, period).then(setData).catch(() => {});
  }, [leagueId, period]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  if (!data) return null;
  const top = data.entries.slice(0, 8);
  // Progress bar scaling — points_total is already the true summed
  // value on every tab (WEEK / MONTH / ALL) after backend normalization.
  const maxAbs = Math.max(10, ...top.map((e) => Math.abs(e.points_total)));

  return (
    <div className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)]" data-testid="leaderboard-widget">
      <div className="p-4 md:p-5 border-b border-[var(--stakes-border)] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-mono">LIVE LEADERBOARD</div>
          <h3 className="font-anton text-2xl md:text-3xl uppercase leading-none mt-1">
            {period === "week"
              ? "This Week"
              : period === "month"
              ? "This Month"
              : period === "year"
              ? "All Time"
              : "Live"}
          </h3>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <div className="label-mono">STAKES TODAY</div>
            <div
              className="font-anton text-2xl md:text-3xl leading-none"
              style={{
                color: data.is_challenge_day_today
                  ? "var(--stakes-admin)"
                  : data.is_weekend_today
                  ? "var(--stakes-primary)"
                  : "#fff",
              }}
              data-testid="leaderboard-stakes-today"
            >
              +{data.stakes_yes_today}/{data.stakes_no_today === 0 ? "0" : data.stakes_no_today}
            </div>
          </div>
        </div>
      </div>

      {/* Period toggle */}
      <div
        className="grid grid-cols-3 gap-px bg-[var(--stakes-border)] border-b border-[var(--stakes-border)]"
        data-testid="leaderboard-period-toggle"
      >
        {[
          { key: "week", label: "WEEK" },
          { key: "month", label: "MONTH" },
          // Phase 20.14 cleanup — the "ALL" tab now maps to the API's
          // `period=year` so it renders the accumulated `all_time_points`
          // (year-end score preserved through monthly rollovers). The
          // legacy "cached points_total" view was redundant and has been
          // removed.
          { key: "year", label: "ALL" },
        ].map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`h-10 font-barlow font-black uppercase tracking-wider text-sm hover-flip ${
              period === p.key
                ? "bg-white text-black"
                : "bg-[var(--stakes-surface)] text-[var(--stakes-muted)] hover:text-white"
            }`}
            data-testid={`leaderboard-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="p-4 md:p-5">
        {top.length === 0 ? (
          <div className="label-mono py-4">NO PLAYERS YET</div>
        ) : (
          <div className="space-y-3">
            {top.map((e, idx) => {
              const color = ACCOUNT_COLORS[e.account_type] || "#fff";
              // ALL tab (period=year) — the backend now returns
              // `points_total = all_time_points + current_active_points`
              // (true lifetime total). Just render points_total directly.
              const displayPoints = e.points_total;
              const pct = Math.min(100, (Math.abs(displayPoints) / maxAbs) * 100);
              const negative = displayPoints < 0;
              const weeklyWins = e.yearly_weekly_wins || 0;
              const monthlyWins = e.yearly_monthly_wins || 0;
              const wearsCrown = !!e.has_weekly_crown;
              return (
                <div
                  key={e.user_id}
                  className="flex flex-col gap-2"
                  data-testid={`leaderboard-row-${e.user_id}`}
                >
                  {/* TOP-LINE — 3-column safe layout (rank+name | trophies | points) */}
                  <div className="flex items-start justify-between gap-2 sm:gap-3">
                    {/* LEFT — rank + crown + name + role */}
                    <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="font-anton text-xl md:text-3xl text-[var(--stakes-muted)] flex-shrink-0 leading-none pt-1">
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                      {wearsCrown && (
                        <span
                          className="font-anton text-xl md:text-2xl leading-none flex-shrink-0 pt-1"
                          title="Reigning weekly champion"
                          data-testid={`leaderboard-crown-${e.user_id}`}
                        >
                          👑
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="font-anton text-lg sm:text-xl md:text-2xl uppercase leading-none break-words whitespace-normal wrap-words">{e.name}</div>
                        <div className="label-mono mt-1" style={{ color }}>
                          {ACCOUNT_LABEL[e.account_type]}
                        </div>
                      </div>
                    </div>

                    {/* CENTER — yearly trophy case (hidden if zero on mobile to save space) */}
                    {(weeklyWins > 0 || monthlyWins > 0) && (
                      <div
                        className="hidden sm:flex flex-col items-center justify-center px-2 flex-shrink-0"
                        data-testid={`leaderboard-trophies-${e.user_id}`}
                      >
                        <div className="label-mono">{`[ 👑 x${weeklyWins} | 🏆 x${monthlyWins} ]`}</div>
                      </div>
                    )}

                    {/* RIGHT — point total, scales to 4 digits */}
                    <div className="text-right flex-shrink-0 max-w-[28%] sm:max-w-[20%]">
                      <div
                        className="font-anton text-xl md:text-3xl leading-none tabular-nums break-words"
                        style={{ color: negative ? "var(--stakes-primary)" : "#fff" }}
                        data-testid={`leaderboard-points-${e.user_id}`}
                      >
                        {displayPoints > 0 ? "+" : ""}{displayPoints}
                      </div>
                      <div className="label-mono mt-1">PTS</div>
                    </div>
                  </div>

                  {/* Mobile-only condensed trophy bracket directly under the name line */}
                  {(weeklyWins > 0 || monthlyWins > 0) && (
                    <div
                      className="sm:hidden label-mono pl-7"
                      data-testid={`leaderboard-trophies-mobile-${e.user_id}`}
                    >
                      [ 👑 x{weeklyWins} | 🏆 x{monthlyWins} ]
                    </div>
                  )}

                  {/* Progress bar — full-width, unobstructed */}
                  <div className="h-2 md:h-3 bg-[var(--stakes-surface-2)] border border-[var(--stakes-border)] relative">
                    <div
                      className="absolute top-0 bottom-0"
                      style={{
                        width: `${pct}%`,
                        background: negative ? "var(--stakes-primary)" : color,
                        right: negative ? "50%" : "auto",
                        left: negative ? "auto" : "0",
                      }}
                    />
                    <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[var(--stakes-border)]" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
